import { useState, useEffect, useRef, useCallback } from "react";
import { Analytics } from "@vercel/analytics/react";
import "./App.css";
import { SERVICES, SKILLS, PROJECTS } from "./data";
import { ICONS } from "./icons";

// Nav follows page order
const NAV_LINKS = [
  { id: "impact", label: "Impact" },
  { id: "about", label: "About" },
  { id: "services", label: "Services" },
  { id: "contact", label: "Contact" },
];

const SOCIALS = [
  { label: "LinkedIn", href: "https://www.linkedin.com/in/preciousoben", icon: "linkedin" },
  { label: "GitHub", href: "https://github.com/preciousoben", icon: "github" },
  { label: "X (Twitter)", href: "https://twitter.com/NerdOffDuty", icon: "x" },
];

// The two About cards. Each has its own tools orbiting a centre mark (none repeat the hero pills).
const CARD_DECK = [
  { center: "logo", tools: ["Looker", "Tableau", "Airflow", "n8n", "Git", "Excel"] },
  { center: "chart", tools: ["PostgreSQL", "R", "GCP", "Power Automate", "Looker Studio", "Dynamics 365"] },
];

const SITE_TITLE = "Precious Oben | Data Analyst & Data Engineer";
const slugFromPath = () => {
  const m = window.location.pathname.match(/^\/work\/([^/]+)\/?$/);
  return m ? m[1] : null;
};

// Things that orbit the name in the hero. "card" is the little dashboard card.
const ORBITERS = [
  { kind: "card" },
  { kind: "pill", label: "SQL", tone: "teal" },
  { kind: "pill", label: "Python", tone: "cream" },
  { kind: "pill", label: "dbt", tone: "teal" },
  { kind: "pill", label: "Snowflake", tone: "cream" },
  { kind: "pill", label: "BigQuery", tone: "teal" },
  { kind: "pill", label: "Power BI", tone: "cream" },
];

// ─── HELPERS ─────────────────────────────────────────────────────────────────
const prefersReducedMotion = () =>
  typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;

const canHover = () =>
  typeof window !== "undefined" && window.matchMedia?.("(hover: hover) and (pointer: fine)").matches;

function useInView(threshold = 0.15, rootMargin = "0px") {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    if (prefersReducedMotion() || typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setInView(true); obs.disconnect(); }
    }, { threshold, rootMargin });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold, rootMargin]);
  return [ref, inView];
}

// variant "rise" is the bigger reveal used on the Impact section
function FadeIn({ children, delay = 0, className = "", variant = "fade" }) {
  const rise = variant === "rise";
  const [ref, inView] = useInView(rise ? 0.18 : 0.12, rise ? "0px 0px -10% 0px" : "0px");
  return (
    <div ref={ref} className={`${rise ? "rise" : "fade"} ${inView ? "is-in" : ""} ${className}`} style={{ transitionDelay: `${delay}s` }}>
      {children}
    </div>
  );
}

// Heading whose lines slide up out of a mask when scrolled into view
function RevealHeading({ lines }) {
  const [ref, inView] = useInView(0.4);
  return (
    <h2 ref={ref} className={`reveal-heading ${inView ? "is-in" : ""}`}>
      {lines.map((l, i) => (
        <span key={i} className="reveal-line"><span style={{ transitionDelay: `${i * 0.12}s` }}>{l}</span></span>
      ))}
    </h2>
  );
}

// Home logo: "PO" monogram with a small dot orbiting it, like the hero
function HomeLogo({ onClick }) {
  return (
    <button className="home-logo" onClick={onClick} aria-label="Back to top">
      <svg viewBox="0 0 44 44" width="44" height="44" aria-hidden="true">
        <circle cx="22" cy="22" r="19" className="logo-ring" />
        <text x="22" y="27" textAnchor="middle" className="logo-text">P<tspan className="logo-o">O</tspan></text>
        <g className="logo-orbit"><circle cx="22" cy="3" r="2.6" className="logo-dot" /></g>
      </svg>
    </button>
  );
}

// Counts a metric like "$729K", "90.2%", "21,013" or "3-layer" up from 0.
// Values with more than one number ("2 days to 4 hrs") are shown as-is.
const NUM_RE = /^([^\d]*)(\d[\d,]*(?:\.\d+)?)([^\d]*)$/;

function CountUp({ value, duration = 1300 }) {
  const match = NUM_RE.exec(value);
  const [ref, inView] = useInView(0.4);
  const [shown, setShown] = useState(match ? null : value);

  useEffect(() => {
    if (!match) return;
    const [, pre, num, post] = match;
    const target = parseFloat(num.replace(/,/g, ""));
    const decimals = num.includes(".") ? num.split(".")[1].length : 0;
    const useCommas = num.includes(",");
    const fmt = (n) => {
      const s = n.toFixed(decimals);
      return pre + (useCommas ? Number(s).toLocaleString("en-US", { minimumFractionDigits: decimals }) : s) + post;
    };
    if (!inView) { setShown(fmt(0)); return; }
    if (prefersReducedMotion()) { setShown(value); return; }
    let raf;
    const start = performance.now();
    const tick = (now) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - t, 3);
      setShown(t === 1 ? value : fmt(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inView, value]);

  return <span ref={ref} className="countup" aria-label={value}>{shown ?? value}</span>;
}

// Sticker-style card: lifts, rotates a hair and its shadow deepens on hover (CSS only).
// lean="left" | "right" alternates the rotation so a stack of cards feels hand-placed.
function TiltCard({ children, className = "", id, lean = "left" }) {
  return (
    <div id={id} className={`sticker sticker-${lean} ${className}`}>
      {children}
    </div>
  );
}

// ─── HERO ORBIT ──────────────────────────────────────────────────────────────
// Items travel on an ellipse around the hero text. Items on the far side of the
// ring are smaller and fainter, which gives a gentle 3D feel.
const ORBIT_SECONDS = 48;

function Orbit({ centerRef, wideRef }) {
  const layerRef = useRef(null);
  const itemRefs = useRef([]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer || !centerRef.current || !wideRef.current) return;
    const reduce = prefersReducedMotion();
    const hover = canHover();
    let raf = null;
    let visible = true;
    let geo = { cx: 0, cy: 0, rx: 0, ry: 0 };
    let mouse = { x: 0, y: 0 }, eased = { x: 0, y: 0 };
    const start = performance.now();
    const n = ORBITERS.length;

    const measure = () => {
      const small = window.innerWidth <= 760;
      // Desktop: ring goes around the whole intro. Mobile: around the name only.
      const target = small ? centerRef.current : wideRef.current;
      const lr = layer.getBoundingClientRect();
      const cr = target.getBoundingClientRect();
      geo = {
        cx: cr.left - lr.left + cr.width / 2,
        cy: cr.top - lr.top + cr.height / 2,
        rx: small ? lr.width / 2 - 36 : cr.width / 2 + 170,
        ry: small ? cr.height / 2 + 62 : cr.height / 2 + 84,
      };
    };

    const draw = (now) => {
      const t = reduce ? 0 : (now - start) / 1000;
      eased.x += (mouse.x - eased.x) * 0.06;
      eased.y += (mouse.y - eased.y) * 0.06;
      for (let i = 0; i < n; i++) {
        const el = itemRefs.current[i];
        if (!el) continue;
        const angle = (i / n) * Math.PI * 2 + (t / ORBIT_SECONDS) * Math.PI * 2;
        const depth = (Math.sin(angle) + 1) / 2; // 0 = far side (top), 1 = near side (bottom)
        const x = geo.cx + Math.cos(angle) * geo.rx + eased.x * (6 + depth * 14);
        const y = geo.cy + Math.sin(angle) * geo.ry + eased.y * (6 + depth * 14);
        const scale = 0.78 + depth * 0.27;
        el.style.transform = `translate(${x.toFixed(1)}px, ${y.toFixed(1)}px) translate(-50%, -50%) scale(${scale.toFixed(3)})`;
        el.style.opacity = (0.28 + depth * 0.37).toFixed(3);
      }
    };

    const loop = (now) => {
      draw(now);
      raf = visible && !reduce ? requestAnimationFrame(loop) : null;
    };

    const onMove = (e) => {
      mouse = { x: (e.clientX / window.innerWidth) * 2 - 1, y: (e.clientY / window.innerHeight) * 2 - 1 };
    };
    const onLeave = () => { mouse = { x: 0, y: 0 }; };
    const onResize = () => { measure(); if (!raf) draw(performance.now()); };

    measure();
    draw(start);
    if (!reduce) raf = requestAnimationFrame(loop);

    // Pause when the hero is off screen
    const hero = layer.closest(".hero");
    const io = typeof IntersectionObserver !== "undefined" ? new IntersectionObserver(([e]) => {
      visible = e.isIntersecting;
      if (visible && !raf && !reduce) raf = requestAnimationFrame(loop);
    }) : null;
    io?.observe(hero);
    window.addEventListener("resize", onResize);
    if (hover && !reduce) { hero.addEventListener("mousemove", onMove); hero.addEventListener("mouseleave", onLeave); }
    // Fonts can change the text size after first paint
    document.fonts?.ready.then(onResize);

    return () => {
      if (raf) cancelAnimationFrame(raf);
      io?.disconnect();
      window.removeEventListener("resize", onResize);
      hero.removeEventListener("mousemove", onMove);
      hero.removeEventListener("mouseleave", onLeave);
    };
  }, [centerRef, wideRef]);

  return (
    <div className="orbit" ref={layerRef} aria-hidden="true">
      {ORBITERS.map((o, i) => (
        <span key={o.label || o.kind} className="orbiter" ref={(el) => (itemRefs.current[i] = el)}>
          {o.kind === "card"
            ? <span className="orbit-card"><span>📊</span></span>
            : <span className={`pill tone-${o.tone}`}>{o.label}</span>}
        </span>
      ))}
      <span className="spark spark-1">+</span>
      <span className="spark spark-2">✦</span>
      <span className="spark spark-3" />
      <span className="spark spark-4">+</span>
    </div>
  );
}

// ─── ABOUT CARD DECK ─────────────────────────────────────────────────────────
// Two stacked cards that swap places every few seconds, or on click/tap.
function CardDeck() {
  const [front, setFront] = useState(0);
  const [swapping, setSwapping] = useState(false);
  const paused = useRef(false);

  const swap = useCallback(() => {
    if (prefersReducedMotion()) { setFront((f) => 1 - f); return; }
    setSwapping(true);
    setFront((f) => 1 - f);
    setTimeout(() => setSwapping(false), 900);
  }, []);

  useEffect(() => {
    if (prefersReducedMotion()) return;
    const id = setInterval(() => { if (!paused.current && !document.hidden) swap(); }, 6000);
    return () => clearInterval(id);
  }, [swap]);

  return (
    <div
      className={`deck ${swapping ? "is-swapping" : ""}`}
      onClick={swap}
      onMouseEnter={() => (paused.current = true)}
      onMouseLeave={() => (paused.current = false)}
      aria-hidden="true"
    >
      {CARD_DECK.map((card, i) => (
        <div key={i} className={`deck-card deck-card-${i} ${front === i ? "is-front" : "is-back"}`}>
          <div className="tool-orbit">
            {card.tools.map((t, j) => (
              <span key={t} className="tool-slot" style={{ "--a": `${(360 / card.tools.length) * j}deg` }}>
                <span className={`tool-pill ${j % 2 ? "tone-cream" : "tone-teal"}`}>{t}</span>
              </span>
            ))}
          </div>
          {card.center === "logo" ? (
            <svg className="profile-mono" viewBox="0 0 44 44">
              <circle cx="22" cy="22" r="19" className="logo-ring" />
              <text x="22" y="27" textAnchor="middle" className="logo-text">P<tspan className="logo-o">O</tspan></text>
              <g className="logo-orbit"><circle cx="22" cy="3" r="2.6" className="logo-dot" /></g>
            </svg>
          ) : (
            <span className="deck-chart">📊</span>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── CASE STUDY OVERLAY ──────────────────────────────────────────────────────
function CopyLink({ slug }) {
  const [copied, setCopied] = useState(false);
  const url = `${window.location.origin}/work/${slug}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 2000); }
    catch (e) { window.prompt("Copy this link:", url); }
  };
  return <button className="close-btn" onClick={copy} aria-live="polite">{copied ? "Link copied ✓" : "Copy link"}</button>;
}

function CaseStudy({ project, onClose }) {
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const isOpen = lightboxIndex !== null;
  const images = project.images || [];
  const labels = project.imageLabels || images.map((_, i) => `Image ${i + 1}`);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") isOpen ? setLightboxIndex(null) : onClose();
      if (e.key === "ArrowRight" && isOpen) setLightboxIndex((i) => (i + 1) % images.length);
      if (e.key === "ArrowLeft" && isOpen) setLightboxIndex((i) => (i - 1 + images.length) % images.length);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isOpen, images.length, onClose]);

  return (
    <>
      {isOpen && (
        <div className="lightbox" onClick={() => setLightboxIndex(null)}>
          <img src={images[lightboxIndex]} alt={labels[lightboxIndex]} />
          {images.length > 1 && (
            <>
              <button className="lb-arrow lb-prev" aria-label="Previous image"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i - 1 + images.length) % images.length); }}>←</button>
              <button className="lb-arrow lb-next" aria-label="Next image"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((i) => (i + 1) % images.length); }}>→</button>
              <p className="lb-caption">{labels[lightboxIndex]} · {lightboxIndex + 1} / {images.length}</p>
            </>
          )}
          <button className="close-btn lb-close" onClick={() => setLightboxIndex(null)}>Close ×</button>
        </div>
      )}

      <div className="overlay" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
        <div className="overlay-panel" role="dialog" aria-modal="true" aria-labelledby="cs-title">
          <div className="overlay-head">
            <div>
              <p className="label">{project.client}</p>
              <h2 id="cs-title">{project.title}</h2>
            </div>
            <div className="overlay-actions">
              <CopyLink slug={project.slug} />
              <button onClick={onClose} className="close-btn">Close ×</button>
            </div>
          </div>

          <div className="tags">{project.tags.map((t) => <span key={t} className="tag">{t}</span>)}</div>

          <div className="impact-strip" style={{ gridTemplateColumns: `repeat(${project.impact.length}, 1fr)` }}>
            {project.impact.map((m) => (
              <div key={m.label}>
                <p className="metric metric-lg"><CountUp value={m.value} /></p>
                <p className="label">{m.label}</p>
              </div>
            ))}
          </div>

          <div className="overlay-text">
            {project.detail.split("\n\n").map((para, i) => <p key={i}>{para}</p>)}
          </div>

          {project.showDashboard !== false && (
            images.length > 0 ? (
              <div>
                <p className="label" style={{ marginBottom: "1rem" }}>{images.length > 1 ? "Project visuals" : "Dashboard"}</p>
                <div className="thumbs" style={{ gridTemplateColumns: images.length > 1 ? `repeat(${Math.min(images.length, 3)}, 1fr)` : "1fr" }}>
                  {images.map((src, idx) => (
                    <button key={src} className="thumb" onClick={() => setLightboxIndex(idx)}>
                      <img src={src} alt={labels[idx]} loading="lazy" />
                      <span className="label">{labels[idx]}</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="empty-shots"><p className="label">Dashboard screenshots coming soon</p></div>
            )
          )}
        </div>
      </div>
    </>
  );
}

// ─── MAIN ────────────────────────────────────────────────────────────────────
export default function PreciousOben() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [formData, setFormData] = useState({ email: "", subject: "", body: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error
  const [activeProject, setActiveProject] = useState(() => {
    const slug = typeof window !== "undefined" ? slugFromPath() : null;
    return PROJECTS.find((p) => p.slug === slug) || null;
  });
  const [showAll, setShowAll] = useState(false);
  const coreRef = useRef(null);
  const introRef = useRef(null);

  const visibleProjects = showAll ? PROJECTS : PROJECTS.slice(0, 3);

  // Each case study has its own URL (/work/<slug>) so it can be linked from a resume
  const openProject = (p) => {
    setActiveProject(p);
    if (window.location.pathname !== `/work/${p.slug}`) window.history.pushState({ slug: p.slug }, "", `/work/${p.slug}`);
  };
  const closeProject = useCallback(() => {
    setActiveProject(null);
    if (slugFromPath()) window.history.pushState({}, "", "/#impact");
  }, []);

  useEffect(() => {
    const onPop = () => setActiveProject(PROJECTS.find((p) => p.slug === slugFromPath()) || null);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    document.title = activeProject ? `${activeProject.title} | Precious Oben` : SITE_TITLE;
    if (activeProject && PROJECTS.indexOf(activeProject) >= 3) setShowAll(true);
  }, [activeProject]);

  // Nav shrinks and gains a background once the hero is scrolled past
  useEffect(() => {
    const onScroll = () => {
      const hero = document.getElementById("hero");
      const limit = hero ? hero.offsetHeight - 90 : 400;
      setScrolled(window.scrollY > limit);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => { window.removeEventListener("scroll", onScroll); window.removeEventListener("resize", onScroll); };
  }, []);

  const scrollTo = useCallback((id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth" });
  }, []);

  const openChat = () => { scrollTo("contact"); setTimeout(() => setContactOpen(true), 600); };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!formData.email || !formData.subject || !formData.body) return;
    setStatus("sending");
    try {
      const res = await fetch("https://formspree.io/f/xreajazj", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ email: formData.email, subject: formData.subject, message: formData.body, _replyto: formData.email }),
      });
      if (!res.ok) throw new Error(res.statusText);
      setStatus("sent");
      setTimeout(() => { setStatus("idle"); setFormData({ email: "", subject: "", body: "" }); setContactOpen(false); }, 3500);
    } catch (err) {
      setStatus("error");
    }
  };

  return (
    <div className="site">
      {activeProject && <CaseStudy project={activeProject} onClose={closeProject} />}

      {/* ── NAV ── */}
      <nav className={`nav ${scrolled ? "is-scrolled" : ""} ${menuOpen ? "is-open" : ""}`} aria-label="Main">
        <div className="nav-inner">
          <HomeLogo onClick={() => { scrollTo("hero"); setMenuOpen(false); }} />
          <div className="nav-links">
            {NAV_LINKS.map((l) => (
              <button key={l.id} className="nav-btn" onClick={() => scrollTo(l.id)}>{l.label}</button>
            ))}
          </div>
          <div className="nav-socials">
            {SOCIALS.map((l) => (
              <a key={l.icon} href={l.href} target="_blank" rel="noreferrer" aria-label={l.label}>
                <svg viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[l.icon]} /></svg>
              </a>
            ))}
          </div>
          <button className="hamburger" aria-label={menuOpen ? "Close menu" : "Open menu"} aria-expanded={menuOpen} onClick={() => setMenuOpen((v) => !v)}>
            <span /><span /><span />
          </button>
        </div>
        {menuOpen && (
          <div className="mobile-menu">
            {NAV_LINKS.map((l) => (
              <button key={l.id} className="nav-btn" onClick={() => { scrollTo(l.id); setMenuOpen(false); }}>{l.label}</button>
            ))}
          </div>
        )}
      </nav>

      {/* ── HERO ── */}
      <section id="hero" className="hero">
        <Orbit centerRef={coreRef} wideRef={introRef} />
        <div className="hero-text" ref={introRef}>
          <div className="hero-core" ref={coreRef}>
            <p className="eyebrow">Hey there, I'm</p>
            <h1>Precious<br /><em>Oben.</em></h1>
          </div>
          <p className="hero-tagline">
            Data Analyst &amp; Data Engineer who gets a little too excited about clean pipelines and dashboards that actually get used.
          </p>
          <div className="hero-ctas">
            <button className="btn btn-solid" onClick={() => scrollTo("impact")}>See my work →</button>
            <button className="btn btn-outline" onClick={openChat}>Let's chat</button>
          </div>
        </div>
      </section>

      {/* ── IMPACT ── */}
      <section id="impact" className="section">
        <FadeIn>
          <p className="label section-label">Impact</p>
        </FadeIn>
        <RevealHeading lines={["Work that moved", "the needle"]} />

        <div className="projects">
          {visibleProjects.map((p, i) => (
            <FadeIn key={p.id} variant="rise" delay={i >= 3 ? (i - 3) * 0.12 : 0} className={i % 2 ? "rise-right" : "rise-left"}>
              <TiltCard className="project-card" id={`case-${p.id}`} lean={i % 2 ? "right" : "left"}>
                <div className="project-head">
                  <div>
                    <p className="label">{p.client}</p>
                    <h3>{p.title}</h3>
                  </div>
                  <div className="tags tags-right">{p.tags.map((t) => <span key={t} className="tag">{t}</span>)}</div>
                </div>
                <p className="project-summary">{p.summary}</p>
                <div className="impact-grid" style={{ gridTemplateColumns: `repeat(${p.impact.length}, 1fr)` }}>
                  {p.impact.map((m) => (
                    <div key={m.label}>
                      <p className="metric"><CountUp value={m.value} /></p>
                      <p className="label">{m.label}</p>
                    </div>
                  ))}
                </div>
                <a className="read-more-btn" href={`/work/${p.slug}`} onClick={(e) => { e.preventDefault(); openProject(p); }}>Read full case study →</a>
              </TiltCard>
            </FadeIn>
          ))}
        </div>

        {PROJECTS.length > 3 && (
          <FadeIn>
            <div className="center">
              <button
                className={`btn ${showAll ? "btn-outline" : "btn-solid"}`}
                aria-expanded={showAll}
                onClick={() => {
                  if (showAll) document.getElementById(`case-${PROJECTS[2].id}`)?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" });
                  setShowAll((v) => !v);
                }}
              >
                {showAll ? "Show less" : "More work →"}
              </button>
            </div>
          </FadeIn>
        )}
      </section>

      {/* ── ABOUT ── */}
      <section id="about" className="section">
        <div className="about-grid">
          <FadeIn className="about-card-col">
            <CardDeck />
          </FadeIn>

          <div>
            <FadeIn>
              <p className="label section-label">About</p>
            </FadeIn>
            <RevealHeading lines={["I turn messy data into", "something people trust"]} />
            <FadeIn delay={0.2}>
              <p className="body-lg about-lede">
                I design and build data infrastructure: pipelines, transformations, and dashboards that businesses can actually rely on. Whether it's automating a broken reporting process, migrating data across enterprise systems, or modeling a clean analytics layer in dbt, I work across the full stack from ingestion to insight.
              </p>
              <p className="body-lg">
                I hold a master's degree in Computer Engineering and analytics certifications including Power BI (PL-300), with dbt in progress.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="services" className="section">
        <FadeIn>
          <p className="label section-label">Services</p>
          <h2>What I can do<br /><em>for you</em></h2>
        </FadeIn>
        <div className="services-grid">
          {SERVICES.map((s, i) => (
            <FadeIn key={s.number} delay={i * 0.1}>
              <div className="service-card">
                <p className="label teal">{s.number}</p>
                <h3>{s.title}</h3>
                <p className="service-desc">{s.description}</p>
                <div className="service-includes">
                  <p className="label teal">Includes</p>
                  <ul>{s.deliverables.map((d) => <li key={d}>{d}</li>)}</ul>
                </div>
              </div>
            </FadeIn>
          ))}
        </div>
        <FadeIn delay={0.2}>
          <div className="cta-band">
            <div>
              <p className="cta-title">Not sure which fits your situation?</p>
              <p className="cta-sub">Reach out and describe what you're working with. I'll tell you what makes sense.</p>
            </div>
            <button className="btn btn-solid" onClick={openChat}>Talk to me</button>
          </div>
        </FadeIn>
      </section>

      {/* ── STACK ── */}
      <section id="stack" className="section">
        <FadeIn>
          <p className="label section-label">Stack</p>
          <h2>Tools I reach for</h2>
        </FadeIn>
        <div className="skills-grid">
          {SKILLS.map((g, i) => (
            <FadeIn key={g.category} delay={i * 0.08}>
              <div className="skill-group">
                <p className="label teal">{g.category}</p>
                <div className="tags">{g.items.map((it) => <span key={it} className="tag">{it}</span>)}</div>
              </div>
            </FadeIn>
          ))}
        </div>
      </section>

      {/* ── CONTACT ── */}
      <section id="contact" className="section">
        <FadeIn>
          <p className="label section-label">Get in touch</p>
          <h2>Let's make something<br /><em>that matters</em></h2>
          <button className={`btn contact-cta ${contactOpen ? "btn-solid" : "btn-outline"}`} aria-expanded={contactOpen} onClick={() => setContactOpen((v) => !v)}>
            {contactOpen ? "Close" : "☕ Let's have a quick chat over coffee"}
          </button>
        </FadeIn>

        {contactOpen && (
          <div className="contact-box">
            {status === "sent" ? (
              <div className="sent" role="status">
                <p className="sent-title">Message sent ✓</p>
                <p className="label teal">I'll be in touch soon.</p>
              </div>
            ) : (
              <form onSubmit={handleSend} className="contact-form">
                <label>
                  <span className="label teal">Your email</span>
                  <input type="email" required className="contact-field" placeholder="you@company.com" value={formData.email}
                    onChange={(e) => setFormData((p) => ({ ...p, email: e.target.value }))} />
                </label>
                <label>
                  <span className="label teal">Subject</span>
                  <input type="text" required className="contact-field" placeholder="What's this about?" value={formData.subject}
                    onChange={(e) => setFormData((p) => ({ ...p, subject: e.target.value }))} />
                </label>
                <label>
                  <span className="label teal">Message</span>
                  <textarea rows={5} required className="contact-field" placeholder="Tell me about your project, role, or idea..." value={formData.body}
                    onChange={(e) => setFormData((p) => ({ ...p, body: e.target.value }))} />
                </label>
                {status === "error" && (
                  <p className="form-error" role="alert">That didn't send. Check your connection and try again, or reach me on LinkedIn.</p>
                )}
                <button type="submit" className="btn btn-solid" disabled={status === "sending"}>
                  {status === "sending" ? "Sending..." : "Send"}
                </button>
              </form>
            )}
          </div>
        )}
      </section>

      <aside className="social-rail" aria-label="Find me online">
        {SOCIALS.map((l) => (
          <a key={l.icon} href={l.href} target="_blank" rel="noreferrer" aria-label={l.label} title={l.label}>
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d={ICONS[l.icon]} /></svg>
          </a>
        ))}
        <span className="rail-line" aria-hidden="true" />
      </aside>

      <footer className="footer">
        <span className="label">© {new Date().getFullYear()} Precious Oben</span>
      </footer>
      <Analytics />
    </div>
  );
}
